import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Shield, Award, Sparkles, Eye, Handshake, KeyRound, Phone, Mail, MessageCircle } from "lucide-react";
import { PlatformLayout } from "./PlatformLayout";
import { Brand } from "./Brand";
import { Reveal } from "./Reveal";
import { AnimatedTitle } from "./AnimatedTitle";
import FoundersIntro from "./FoundersIntro";
import { VINCENT_STUDIO } from "./api";

const VINCENT = VINCENT_STUDIO;
const ECOSYSTEME = "https://customer-assets-4nw71qhi.emergentagent.net/job_appart-jardin/artifacts/vlk5pxgh_image-1784364646400.png";
const SEBASTIEN = "https://static.prod-images.emergentagent.com/jobs/8e57cf35-f4fb-40ce-b502-ff37f5224c96/images/02e4d0e3704d478b55c2f139a12b07a4f95b039aa0e0573ca24a3683ee700172.jpeg";

const ENGAGEMENTS = [
  { icon: Eye, title: "Confidentialité", text: "Une commercialisation off-market, discrète et maîtrisée, protégeant la vie privée de nos vendeurs.", img: "https://images.unsplash.com/photo-1677951570313-b0750351c461?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" },
  { icon: Award, title: "Exigence", text: "Une présentation immersive et soignée, digne des plus belles agences internationales.", img: "https://images.unsplash.com/photo-1778731660340-301b17d36c76?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" },
  { icon: Handshake, title: "Accompagnement", text: "Un interlocuteur unique, disponible 7j/7, de la première estimation à la signature.", img: "https://images.unsplash.com/photo-1575956011521-4d7f5cf0b18e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" },
  { icon: Shield, title: "Transparence", text: "Une estimation juste, des conseils sincères et un suivi clair à chaque étape.", img: "https://images.unsplash.com/photo-1523477593243-78bbf626fd3b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" },
  { icon: KeyRound, title: "Exclusivité", text: "Les plus belles opportunités, souvent proposées avant même leur mise sur le marché.", img: "https://images.unsplash.com/photo-1720842675989-3a0afebbba22?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" },
  { icon: Sparkles, title: "Valorisation", text: "Home staging, photographie et storytelling pour révéler le plein potentiel de chaque bien.", img: "https://images.unsplash.com/photo-1776482128172-dd265ad0cb49?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" },
];

const ENGAGEMENTS_BG = "https://images.unsplash.com/photo-1695836065042-c3938a4917ba?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const Section = ({ children, className = "" }) => (
  <section className={`max-w-7xl mx-auto px-6 lg:px-12 ${className}`}>{children}</section>
);

export default function Agence() {
  const { t } = useTranslation();
  return (
    <PlatformLayout>
      {/* En-tête : intro cinématique des fondateurs (remplace la bannière Chantilly) */}
      <FoundersIntro title="notre philosophie" />

      {/* Les fondateurs — l'association */}
      <section className="py-14 sm:py-20 border-b border-white/5" data-testid="agence-fondateurs-intro">
        <Section className="max-w-3xl mx-auto text-center space-y-6">
          <Reveal>
            <span className="calli text-2xl sm:text-3xl text-[#C19A6B]">{t("philo.founders_eyebrow")}</span>
            <AnimatedTitle as="h2" className="text-3xl sm:text-4xl font-light tracking-tight mt-2" text={t("philo.founders_title")} />
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-zinc-300 font-light leading-relaxed text-[15px]">{t("philo.founders_intro")}</p>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="text-lg sm:text-xl text-white font-light italic leading-relaxed">{t("philo.founders_conviction")}</p>
          </Reveal>
        </Section>
      </section>

      {/* Vincent Marette — d'abord */}
      <section id="vincent" className="py-14 sm:py-20">
        <Section className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start lg:items-center">
          <div className="lg:col-span-5 lg:sticky lg:top-28 space-y-6">
            <span className="calli text-lg text-[#C19A6B]">{t("philo.vincent_eyebrow")}</span>
            <AnimatedTitle as="h2" className="text-3xl sm:text-4xl font-light tracking-tight" text="Vincent Marette" />
            <p className="text-sm uppercase tracking-widest text-[#C19A6B]">{t("philo.vincent_role")}</p>
            <div className="w-[85%] space-y-6">
              <img src={VINCENT} alt="Vincent Marette" className="w-full aspect-[4/5] object-cover border border-white/10 rounded-[5px]" style={{ objectPosition: "center 15%" }} />
              <div className="grid grid-cols-3 gap-2">
                <a href="tel:+33688675357" data-testid="agence-call" className="inline-flex items-center justify-center gap-1.5 bg-[#C19A6B] hover:bg-[#d1af84] text-white font-medium uppercase tracking-wider text-xs px-2 py-3 transition-colors"><Phone className="w-3.5 h-3.5" /> {t("philo.call")}</a>
                <a href="mailto:vincent.marette@safti.fr" className="inline-flex items-center justify-center gap-1.5 border border-white/20 hover:bg-white/5 text-white font-medium uppercase tracking-wider text-xs px-2 py-3 transition-colors"><Mail className="w-3.5 h-3.5" /> {t("philo.write")}</a>
                <a href="https://wa.me/33688675357" target="_blank" rel="noopener noreferrer" data-testid="agence-whatsapp" className="inline-flex items-center justify-center gap-1.5 bg-white hover:bg-zinc-200 text-[#050505] font-medium uppercase tracking-wider text-xs px-2 py-3 transition-colors"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp</a>
              </div>
            </div>
          </div>
          <Reveal className="lg:col-span-7 space-y-5 text-zinc-100 font-light leading-relaxed text-[15px]">
            <AnimatedTitle as="h3" className="text-2xl sm:text-3xl font-light text-[#C19A6B] tracking-tight" text={t("philo.vincent_h3")} />
            <p>{t("philo.vincent_p1")}</p>
            <p>{t("philo.vincent_p2")}</p>
            <p>{t("philo.vincent_p3")}</p>
            <p className="text-white text-lg">{t("philo.vincent_p4")}</p>
          </Reveal>
        </Section>
      </section>

      {/* Sébastien Ollagnon — co-fondateur */}
      <section id="sebastien" className="py-14 sm:py-20 border-t border-white/5">
        <Section className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start lg:items-center">
          <Reveal className="lg:col-span-7 space-y-5 text-zinc-100 font-light leading-relaxed text-[15px]">
            <span className="calli text-lg text-[#C19A6B]">{t("philo.seb_eyebrow")}</span>
            <AnimatedTitle as="h2" className="text-3xl sm:text-4xl font-light tracking-tight" text="Sébastien Ollagnon" />
            <p className="text-sm uppercase tracking-widest text-[#C19A6B]">{t("philo.seb_role")}</p>
            <p>{t("philo.seb_p1")}</p>
            <p>{t("philo.seb_p2")}</p>
            <p>{t("philo.seb_p3")}</p>
          </Reveal>
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <div className="w-[85%] mx-auto lg:ml-auto lg:mr-0 space-y-4">
              <img src={SEBASTIEN} alt="Sébastien Ollagnon, co-fondateur d'Off Markette" data-testid="agence-sebastien-img" className="w-full aspect-[4/5] object-cover border border-white/10 rounded-[5px]" />
              <p className="text-center lg:text-right text-xs uppercase tracking-[0.2em] text-zinc-500">{t("philo.seb_caption")}</p>
            </div>
          </div>
        </Section>
      </section>

      {/* OFF MARKETTE — ensuite */}
      <section className="py-14 sm:py-20 bg-white">
        <Section className="text-center mb-12">
          <Reveal>
            <span className="calli text-2xl sm:text-3xl text-[#C19A6B]">{t("philo.philo_eyebrow")}</span>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight mt-2 max-w-3xl mx-auto text-zinc-900"><Brand />{t("philo.philo_title_suffix")}</h2>
          </Reveal>
        </Section>
        <Section className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start lg:items-center">
          <Reveal className="lg:col-span-7 space-y-5 text-zinc-600 font-light leading-relaxed text-[15px] order-2 lg:order-1">
            <p>{t("philo.philo_p1")}</p>
            <p>{t("philo.philo_p2")}</p>
            <p>{t("philo.philo_p3")}</p>
          </Reveal>
          <div className="lg:col-span-5 lg:sticky lg:top-28 order-1 lg:order-2">
            <div className="relative overflow-hidden border border-zinc-200 rounded-[5px]">
              <img src={ECOSYSTEME} alt={t("philo.philo_ecosys_alt")} data-testid="agence-ecosysteme-img" className="w-full aspect-[4/3] object-cover" />
            </div>
          </div>
        </Section>
        <Section className="mt-12 space-y-10">
          <Reveal>
            <p className="calli text-2xl sm:text-3xl text-[#C19A6B] text-center max-w-3xl mx-auto leading-snug">{t("philo.philo_quote")}</p>
          </Reveal>
          <Reveal className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-3xl mx-auto">
            {[{ icon: Eye, t: "Plus discrète" }, { icon: Award, t: "Plus qualitative" }, { icon: Handshake, t: "Plus collaborative & humaine" }].map((x, i) => (
              <div key={x.t} className="flex flex-col items-center text-center gap-3">
                <span className="flex items-center justify-center text-[#C19A6B]"><x.icon className="w-8 h-8" /></span>
                <span className="text-sm uppercase tracking-[0.15em] text-zinc-900 font-medium">{t(`philo.philo_pt${i + 1}`)}</span>
              </div>
            ))}
          </Reveal>
          <Reveal>
            <p className="text-lg sm:text-xl text-zinc-800 font-light italic leading-relaxed text-center max-w-3xl mx-auto">{t("philo.philo_closing")}</p>
          </Reveal>
        </Section>
      </section>

      {/* Engagements */}
      <section className="relative py-14 sm:py-20 overflow-hidden bg-zinc-100">
        <Section className="relative z-10 space-y-10">
          <Reveal className="max-w-2xl">
            <span className="calli text-3xl sm:text-4xl text-[#C19A6B]">{t("philo.engagements_eyebrow")}</span>
            <h2 className="text-3xl sm:text-4xl font-light text-zinc-900 tracking-tight mt-2">{t("philo.engagements_title")}</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="agence-engagements">
            {ENGAGEMENTS.map((e, i) => (
              <motion.div key={e.title} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.55, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }} data-testid={`engagement-card-${i}`} className="group relative overflow-hidden bg-white border border-zinc-200 rounded-[5px] p-8 hover:border-[#C19A6B]/50 hover:shadow-xl hover:shadow-black/5 transition-all">
                <div className="absolute inset-0 bg-cover bg-center opacity-0 group-hover:opacity-100 transition-opacity duration-500 ease-out" style={{ backgroundImage: `url(${e.img})` }} />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: "linear-gradient(to top, rgba(5,5,5,0.92), rgba(5,5,5,0.72))" }} />
                <div className="relative z-10 flex flex-col items-center text-center space-y-3">
                  <e.icon className="w-7 h-7 text-[#C19A6B]" />
                  <h3 className="text-lg font-semibold text-zinc-900 group-hover:text-white transition-colors">{t(`philo.engagements.${i}.title`)}</h3>
                  <p className="text-sm font-light text-zinc-600 group-hover:text-white leading-relaxed transition-colors">{t(`philo.engagements.${i}.text`)}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </Section>
      </section>
    </PlatformLayout>
  );
}
